// EventInspectorPanel — unified inspector for the active event and its node.
// Reads activeEventIndex directly from the Redux store. Every action that
// changes which event is active (step, scrub, play, node click) writes to
// activeEventIndex, and this component re-renders automatically.

import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getCachedEventCatalog } from '../../services/ArtifactLoaderService.ts';
import type { EventCatalogField } from '../../types/moment-artifacts.ts';
import {
  selectActualPath,
  selectSimulationStatus,
  selectActiveEventIndex,
} from '../../store/slices/simulationSlice.ts';
import {
  selectEvents,
  selectExpectedPath,
  selectScenarioLabel,
  selectGiven,
  selectWhen,
  selectThen,
} from '../../store/slices/eventsSlice.ts';
import { selectSelectedNode, selectNode } from '../../store/slices/uiSlice.ts';
import {
  selectNodeById,
  selectLaneById,
  selectFrameByNodeId,
  selectEventRouting,
  selectConnectionsForNode,
  selectAdrLinks,
  selectNodeTypeStyles,
} from '../../store/slices/topologySlice.ts';
import { resolveNodeStyle } from '../../services/styleResolver.ts';
import type { PlatformEvent, ConnectionDef } from '../../types/index.ts';
import type { RootState } from '../../store/index.ts';

// ---------------------------------------------------------------------------
// Payload value renderer
// ---------------------------------------------------------------------------

function PayloadValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (value === null) return <span className="ev-val ev-val-null">null</span>;
  if (value === undefined) return <span className="ev-val ev-val-null">undefined</span>;
  if (typeof value === 'boolean')
    return <span className="ev-val ev-val-bool">{String(value)}</span>;
  if (typeof value === 'number') return <span className="ev-val ev-val-num">{value}</span>;
  if (typeof value === 'string') {
    const display = value.length > 120 ? value.slice(0, 120) + '…' : value;
    return <span className="ev-val ev-val-str">"{display}"</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="ev-val ev-val-empty">[]</span>;
    return (
      <div className="ev-nested" style={{ marginLeft: depth > 0 ? 12 : 0 }}>
        {value.map((item, i) => (
          <div key={i} className="ev-array-item">
            <span className="ev-array-idx">[{i}]</span>
            <PayloadValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="ev-val ev-val-empty">{'{}'}</span>;
    return (
      <div className="ev-nested" style={{ marginLeft: depth > 0 ? 12 : 0 }}>
        {entries.map(([k, v]) => (
          <div key={k} className="ev-kv-row">
            <span className="ev-key">{k}</span>
            <PayloadValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <span className="ev-val">{String(value)}</span>;
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function Section({
  title,
  children,
  muted,
}: {
  title: string;
  children: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className={`ev-section${muted ? ' ev-section-muted' : ''}`}>
      <h4 className="ev-section-title">{title}</h4>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EventInspectorPanel() {
  const dispatch = useDispatch();
  const activeEventIndex = useSelector(selectActiveEventIndex);
  const actualPath = useSelector(selectActualPath);
  const events = useSelector(selectEvents);
  const expectedPath = useSelector(selectExpectedPath);
  const status = useSelector(selectSimulationStatus);
  const selectedNodeId = useSelector(selectSelectedNode);
  const eventRouting = useSelector(selectEventRouting);
  const adrLinks = useSelector(selectAdrLinks);
  const scenarioLabel = useSelector(selectScenarioLabel);
  const given = useSelector(selectGiven);
  const when = useSelector(selectWhen);
  const then = useSelector(selectThen);

  // True when the user has manually selected a node (vs walker-driven focus)
  const isManualSelection = selectedNodeId !== null;

  // Derive IDs from activeEventIndex
  const event: PlatformEvent | undefined =
    activeEventIndex !== null ? events[activeEventIndex] : undefined;
  const eventNodeId: string | null =
    activeEventIndex !== null ? (actualPath[activeEventIndex] ?? null) : null;
  const previousNodeId: string | null =
    activeEventIndex !== null && activeEventIndex > 0 ? actualPath[activeEventIndex - 1] : null;

  // The node to display: event's node if available, else manually selected node
  const displayNodeId = eventNodeId ?? selectedNodeId;

  // Node topology data
  const node = useSelector((state: RootState) =>
    displayNodeId ? selectNodeById(state, displayNodeId) : undefined,
  );
  const lane = useSelector((state: RootState) =>
    node ? selectLaneById(state, node.lane) : undefined,
  );
  const frame = useSelector((state: RootState) =>
    displayNodeId ? selectFrameByNodeId(state, displayNodeId) : undefined,
  );
  const nodeConnections = useSelector((state: RootState) =>
    displayNodeId ? selectConnectionsForNode(state, displayNodeId) : [],
  );
  const topologyNodeStyles = useSelector(selectNodeTypeStyles);

  // Expected vs actual match
  const expectedNode =
    activeEventIndex !== null && activeEventIndex < expectedPath.length
      ? expectedPath[activeEventIndex]
      : null;
  const pathMatch = expectedNode && eventNodeId ? expectedNode === eventNodeId : null;

  // Causal chain: find events referenced by causationEventIds
  const causationEvents = useMemo(() => {
    if (!event || event.causationEventIds.length === 0) return [];
    return event.causationEventIds
      .map((cid) => {
        const idx = events.findIndex((e) => e.eventId === cid);
        return idx >= 0 ? { index: idx, event: events[idx] } : null;
      })
      .filter(Boolean) as { index: number; event: PlatformEvent }[];
  }, [event, events]);

  // Routing targets for this event type
  const routingTargets = event ? (eventRouting[event.eventType] ?? []) : [];

  // Catalog metadata for this event type (producer, consumers, temporal location)
  const catalogEntry = useMemo(() => {
    if (!event) return null;
    const catalog = getCachedEventCatalog();
    return catalog?.events.find((e) => e.eventName === event.eventType) ?? null;
  }, [event]);

  // ── Empty state ──
  if (!displayNodeId && status === 'idle') {
    return (
      <div className="ev-inspector ev-inspector-empty">
        <div className="ev-empty-icon">{null}</div>
        <p className="ev-empty-text">Select a node or start playback to inspect events</p>
      </div>
    );
  }

  if (!node) {
    return (
      <div className="ev-inspector ev-inspector-empty">
        <p className="ev-empty-text">No event at this position</p>
      </div>
    );
  }

  const ts = resolveNodeStyle(node.type, topologyNodeStyles);
  const laneColor = lane?.color ?? '#888';
  const incomingConns = nodeConnections.filter((c: ConnectionDef) => c.to === displayNodeId);
  const outgoingConns = nodeConnections.filter((c: ConnectionDef) => c.from === displayNodeId);

  return (
    <div className="ev-inspector">
      {/* Header: node identity + optional step/event info */}
      <div className="ev-header">
        <div className="ev-header-top">
          {event && <span className="ev-step-badge">STEP {activeEventIndex! + 1}</span>}
          {pathMatch !== null && (
            <span className={`ev-match-badge ${pathMatch ? 'ev-match-pass' : 'ev-match-fail'}`}>
              {pathMatch ? 'PATH OK' : 'MISMATCH'}
            </span>
          )}
          {isManualSelection && (
            <button
              className="ev-deselect-btn"
              onClick={() => dispatch(selectNode(null))}
              aria-label="Deselect node"
            >
              ✕
            </button>
          )}
        </div>
        {event && <div className="ev-event-type">{event.eventType}</div>}
        <div className="ev-node-row">
          <span className="ev-node-icon" style={{ color: ts.fill }}>
            {ts.icon}
          </span>
          <span className="ev-node-label">{node.label}</span>
          <span
            className="ev-lane-badge"
            style={{
              color: laneColor,
              background: `${laneColor}15`,
              borderColor: `${laneColor}30`,
            }}
          >
            {lane?.label ?? node.lane}
          </span>
          <span className="ev-type-label">{ts.label}</span>
        </div>
        {frame && (
          <div className="ev-frame-label">
            {frame.id.toUpperCase()} — {frame.moment}
          </div>
        )}
        {node.detail && <div className="ev-node-description">{node.detail}</div>}
        {/* ADR / aggregate badges */}
        {((node.adrs?.length ?? 0) > 0 || node.aggregate) && (
          <div className="ev-badge-row">
            {node.aggregate && (
              <span className="detail-badge detail-badge-aggregate">{node.aggregate}</span>
            )}
            {node.adrs?.map((adr) => {
              const url = adrLinks[adr];
              return url ? (
                <a
                  key={adr}
                  className="detail-badge detail-badge-adr"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {adr}
                </a>
              ) : (
                <span key={adr} className="detail-badge detail-badge-adr">
                  {adr}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Scenario narrative (BDD) ── */}
      {(given || when || then) && (
        <Section title="Scenario">
          <div className="ev-meta-grid">
            {scenarioLabel && (
              <div className="ev-meta-row">
                <span className="ev-meta-key">Label</span>
                <span className="ev-meta-val">{scenarioLabel}</span>
              </div>
            )}
            {given && (
              <div className="ev-meta-row">
                <span className="ev-meta-key">Given</span>
                <span className="ev-meta-val">{given}</span>
              </div>
            )}
            {when && (
              <div className="ev-meta-row">
                <span className="ev-meta-key">When</span>
                <span className="ev-meta-val">{when}</span>
              </div>
            )}
            {then && (
              <div className="ev-meta-row">
                <span className="ev-meta-key">Then</span>
                <span className="ev-meta-val">{then}</span>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── Event sections (only when an event is available) ── */}
      {event && (
        <>
          {/* Envelope metadata */}
          <Section title="Envelope">
            <div className="ev-meta-grid">
              <div className="ev-meta-row">
                <span className="ev-meta-key">eventId</span>
                <span className="ev-meta-val ev-mono">{event.eventId}</span>
              </div>
              <div className="ev-meta-row">
                <span className="ev-meta-key">productSource</span>
                <span className="ev-meta-val">
                  <span className="ev-source-badge">{event.productSource}</span>
                </span>
              </div>
              <div className="ev-meta-row">
                <span className="ev-meta-key">sessionId</span>
                <span className="ev-meta-val ev-mono">{event.sessionId}</span>
              </div>
              <div className="ev-meta-row">
                <span className="ev-meta-key">correlationId</span>
                <span className="ev-meta-val ev-mono">{event.correlationId}</span>
              </div>
              <div className="ev-meta-row">
                <span className="ev-meta-key">timestamp</span>
                <span className="ev-meta-val ev-mono">{event.timestamp}</span>
              </div>
              <div className="ev-meta-row">
                <span className="ev-meta-key">version</span>
                <span className="ev-meta-val ev-mono">v{event.version}</span>
              </div>
            </div>
          </Section>

          {/* Payload */}
          <Section title="Payload">
            <div className="ev-payload">
              {Object.keys(event.payload).length === 0 ? (
                <span className="ev-val ev-val-empty">{'{}'}</span>
              ) : (
                (() => {
                  const fieldMap = new Map(
                    (catalogEntry?.schema?.fields ?? []).map((f: EventCatalogField) => [f.name, f]),
                  );

                  return Object.entries(event.payload).map(([k, v]) => {
                    const fieldDef = fieldMap.get(k);
                    const isDeprecated = !!fieldDef?.deprecated;

                    return (
                      <div key={k} className="ev-kv-row">
                        <span className={`ev-key ${isDeprecated ? 'ev-key-deprecated' : ''}`}>
                          {isDeprecated && <span className="ev-deprecated-badge">deprecated</span>}
                          {k}
                        </span>
                        <PayloadValue value={v} />
                        {isDeprecated && fieldDef?.deprecated && (
                          <span className="ev-deprecated-hint">
                            Use {fieldDef.deprecated.replacement}
                          </span>
                        )}
                      </div>
                    );
                  });
                })()
              )}
            </div>
          </Section>

          {/* Causal chain */}
          {event.causationEventIds.length > 0 && (
            <Section title="Causation Chain">
              <div className="ev-causation-list">
                {event.causationEventIds.map((cid) => {
                  const found = causationEvents.find((c) => c.event.eventId === cid);
                  return (
                    <div key={cid} className="ev-causation-item">
                      <span className="ev-causation-arrow">←</span>
                      <div className="ev-causation-detail">
                        <span className="ev-causation-type">
                          {found ? found.event.eventType : 'unknown'}
                        </span>
                        <span className="ev-causation-id">{cid}</span>
                        {found && <span className="ev-causation-step">Step {found.index + 1}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Routing */}
          <Section title="Routing">
            <div className="ev-routing">
              <div className="ev-meta-row">
                <span className="ev-meta-key">resolved to</span>
                <span className="ev-meta-val ev-mono">{eventNodeId ?? '—'}</span>
              </div>
              {expectedNode && (
                <div className="ev-meta-row">
                  <span className="ev-meta-key">expected</span>
                  <span className="ev-meta-val ev-mono">{expectedNode}</span>
                </div>
              )}
              {routingTargets.length > 0 && (
                <div className="ev-meta-row">
                  <span className="ev-meta-key">routing targets</span>
                  <span className="ev-meta-val ev-mono">{routingTargets.join(', ')}</span>
                </div>
              )}
              {previousNodeId && (
                <div className="ev-meta-row">
                  <span className="ev-meta-key">from node</span>
                  <span className="ev-meta-val ev-mono">{previousNodeId}</span>
                </div>
              )}
            </div>
          </Section>

          {/* Catalog metadata (producer, consumers, temporal location) */}
          {catalogEntry && (
            <Section title="Event Catalog">
              <div className="ev-meta-grid">
                {catalogEntry.producer && (
                  <>
                    <div className="ev-meta-row">
                      <span className="ev-meta-key">producer</span>
                      <span className="ev-meta-val ev-mono">
                        {catalogEntry.producer.context}::{catalogEntry.producer.aggregate}
                      </span>
                    </div>
                    <div className="ev-meta-row">
                      <span className="ev-meta-key">command</span>
                      <span className="ev-meta-val ev-mono">{catalogEntry.producer.command}</span>
                    </div>
                  </>
                )}
                {catalogEntry.consumers?.length > 0 && (
                  <div className="ev-meta-row">
                    <span className="ev-meta-key">consumers</span>
                    <span className="ev-meta-val ev-mono">
                      {catalogEntry.consumers
                        .map((c) => `${c.context} (${c.relationshipType})`)
                        .join(', ')}
                    </span>
                  </div>
                )}
                {catalogEntry.temporalLocation && (
                  <div className="ev-meta-row">
                    <span className="ev-meta-key">moment</span>
                    <span className="ev-meta-val ev-mono">
                      [{catalogEntry.temporalLocation.momentIndex}]{' '}
                      {catalogEntry.temporalLocation.moment}
                    </span>
                  </div>
                )}
              </div>
            </Section>
          )}
        </>
      )}

      {/* Connections (always shown when available) */}
      {(incomingConns.length > 0 || outgoingConns.length > 0) && (
        <Section title="Connections">
          <div className="ev-connections">
            {incomingConns.map((c: ConnectionDef, i: number) => (
              <div key={`in-${i}`} className="ev-conn-row">
                <span className="ev-conn-dir ev-conn-in">IN</span>
                <span className="ev-conn-path">
                  {c.from} → {c.to}
                </span>
                <span className={`ev-conn-style ev-conn-style-${c.style}`}>{c.style}</span>
                {c.label && <span className="ev-conn-label">{c.label}</span>}
              </div>
            ))}
            {outgoingConns.map((c: ConnectionDef, i: number) => (
              <div key={`out-${i}`} className="ev-conn-row">
                <span className="ev-conn-dir ev-conn-out">OUT</span>
                <span className="ev-conn-path">
                  {c.from} → {c.to}
                </span>
                <span className={`ev-conn-style ev-conn-style-${c.style}`}>{c.style}</span>
                {c.label && <span className="ev-conn-label">{c.label}</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Schema contract */}
      {incomingConns.some((c: ConnectionDef) => c.schemaContract) && (
        <Section title="Schema Contract">
          {incomingConns
            .filter((c: ConnectionDef) => c.schemaContract)
            .map((c: ConnectionDef, i: number) => (
              <div key={i} className="ev-schema-contract">
                <div className="ev-meta-row">
                  <span className="ev-meta-key">contractId</span>
                  <span className="ev-meta-val ev-mono">{c.schemaContract!.contractId}</span>
                </div>
                <div className="ev-meta-row">
                  <span className="ev-meta-key">required fields</span>
                  <span className="ev-meta-val ev-mono">
                    {c.schemaContract!.requiredFields.join(', ')}
                  </span>
                </div>
                {c.schemaContract!.fieldTypes && (
                  <div className="ev-meta-row">
                    <span className="ev-meta-key">field types</span>
                    <div className="ev-nested">
                      {Object.entries(c.schemaContract!.fieldTypes).map(([field, type]) => (
                        <div key={field} className="ev-kv-row">
                          <span className="ev-key">{field}</span>
                          <span className="ev-val ev-val-str">{type as string}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
        </Section>
      )}

      {/* Node metadata */}
      <Section title="Node Metadata" muted>
        <div className="ev-meta-grid">
          <div className="ev-meta-row">
            <span className="ev-meta-key">id</span>
            <span className="ev-meta-val ev-mono">{node.id}</span>
          </div>
          <div className="ev-meta-row">
            <span className="ev-meta-key">type</span>
            <span className="ev-meta-val">{ts.label}</span>
          </div>
          {node.aggregate && (
            <div className="ev-meta-row">
              <span className="ev-meta-key">aggregate</span>
              <span className="ev-meta-val">{node.aggregate}</span>
            </div>
          )}
          {node.multiplicity && (
            <div className="ev-meta-row">
              <span className="ev-meta-key">multiplicity</span>
              <span className="ev-meta-val">{node.multiplicity}</span>
            </div>
          )}
          {node.eventTypes && node.eventTypes.length > 0 && (
            <div className="ev-meta-row">
              <span className="ev-meta-key">eventTypes</span>
              <span className="ev-meta-val ev-mono">{node.eventTypes.join(', ')}</span>
            </div>
          )}
          {node.optional && (
            <div className="ev-meta-row">
              <span className="ev-meta-key">optional</span>
              <span className="ev-meta-val ev-mono">true</span>
            </div>
          )}
          {node.terminal && (
            <div className="ev-meta-row">
              <span className="ev-meta-key">terminal</span>
              <span className="ev-meta-val ev-mono">true</span>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
